/* -----------------Types--------------- */

import { getNumPercenetageAnswered } from "@features/exam";
import useAIFeedbackStore from "@features/session/hooks/useAIFeedbackStore";
import type {
	Choice,
	NumChoicesRequired,
	RenderableItemTypes,
} from "@memorang/types/src/session/new-session";
import { Box } from "@memorang/ui";
import { useEffect, useState } from "react";
import { DraggableList } from "../itemtypes/question/variants/Sequence";
/* -----------------Components--------------- */
import ChoiceListItem from "./ChoiceListItem";
interface Props {
	choices: Choice[];
	handleSelectChoice: (
		choiceId: string,
		crossingOut?: boolean,
		currentOrders?: string[],
	) => void;
	selectedChoiceIds?: string[];
	correctAnswerIds?: string[];
	crossedOutChoiceIds?: string[];
	multiSelect?: boolean;
	isAnswered?: boolean;
	useCardWrapper?: boolean;
	showCrossOut?: boolean;
	mappedItemType?: RenderableItemTypes;
	itemId: number;
	correctAnswerOrders?: string[];
	numChoicesRequired?: NumChoicesRequired;
}

const ChoicesList = ({
	choices,
	handleSelectChoice,
	selectedChoiceIds,
	multiSelect,
	correctAnswerIds,
	crossedOutChoiceIds,
	isAnswered,
	useCardWrapper = true,
	showCrossOut,
	mappedItemType,
	itemId,
	correctAnswerOrders,
	numChoicesRequired,
}: Props) => {
	const isSequence = mappedItemType === "SEQUENCE";
	const isSATA = mappedItemType === "SATA";

	const userResponse = useAIFeedbackStore((store) => store.userResponse);
	const setUserResponse = useAIFeedbackStore((store) => store.setUserResponse);

	const currentUserResponse = userResponse?.[itemId];

	useEffect(() => {
		if (isSequence && !currentUserResponse) {
			setUserResponse(
				itemId,
				choices.map((choice) => choice.stem),
			);
		}
	}, [choices, itemId, setUserResponse, isSequence, currentUserResponse]);

	const [localChoices, setLocalChoices] = useState(choices);

	if (isSequence) {
		const renderItem = (choice: Choice, index: number, drag: () => void) => {
			const isCorrectAnswer = correctAnswerOrders?.[index] === choice.id;
			const status = selectedChoiceIds?.includes(choice.id)
				? "checked"
				: "unchecked";
			const percentageAnswered = getNumPercenetageAnswered(
				choices,
				choice.numFirstSelections ?? 0,
				isAnswered,
			);

			const isSelected = selectedChoiceIds?.includes(choice.id);

			return (
				<ChoiceListItem
					{...choice}
					index={index + 1}
					useCardWrapper={useCardWrapper}
					answered={isAnswered}
					multiSelect={multiSelect}
					status={status}
					isSelected={isSelected}
					showCrossOut={showCrossOut}
					isCorrectAnswer={isCorrectAnswer}
					drag={drag}
					percentageAnswered={percentageAnswered}
					handleSelectChoice={(_crossOut) => {
						// handleSelectChoice(choice.id, crossOut);
					}}
				/>
			);
		};

		const handleReorder = (newItems: Choice[]) => {
			setLocalChoices(newItems);
			setUserResponse(
				itemId,
				newItems.map((item) => item.text),
			);
			handleSelectChoice(
				" ",
				false,
				newItems.map((item) => item.id),
			);
		};

		return (
			<DraggableList
				items={localChoices}
				onReorder={handleReorder}
				renderItem={renderItem}
				answered={isAnswered}
			/>
		);
	}

	return (
		<Box gap={useCardWrapper ? 16 : 0}>
			{choices?.map((choice, index) => {
				const isCorrectAnswer = correctAnswerIds?.includes(choice.id);

				const status = selectedChoiceIds?.includes(choice.id)
					? "checked"
					: "unchecked";
				const isCrossedOut = crossedOutChoiceIds?.includes(choice.id);

				const percentageAnswered = getNumPercenetageAnswered(
					choices,
					choice.numFirstSelections ?? 0,
					isAnswered,
				);
				const isSelected = selectedChoiceIds?.includes(choice.id);

				const disabled = isSATA
					? selectedChoiceIds?.length === numChoicesRequired?.max && !isSelected
					: false;
				return (
					<ChoiceListItem
						key={choice.id}
						{...choice}
						index={index + 1}
						useCardWrapper={useCardWrapper}
						answered={isAnswered}
						disabled={disabled}
						multiSelect={multiSelect}
						status={status}
						isSelected={isSelected}
						showCrossOut={showCrossOut}
						isCorrectAnswer={isCorrectAnswer}
						isCrossedOut={isCrossedOut}
						percentageAnswered={percentageAnswered}
						handleSelectChoice={(crossOut) =>
							handleSelectChoice(choice.id, crossOut)
						}
					/>
				);
			})}
		</Box>
	);
};

export default ChoicesList;
